// What a 401 from the BFF is allowed to mean (busy-my-web#377).
//
// Before this, the interceptor's ONLY answer to a 401 was a full-page trip to `/bff/login`: the
// page unloaded, whatever the user was typing went with it, and — when the identity provider's own
// session had lapsed too — he landed on the IdP's account picker with his account tagged "expired"
// and had to click his way back in. Every one of those 401s was treated as "the session is over".
//
// It is not. The BFF forwards a `userId` header downstream, never a token, so a 401 that a
// downstream service produced for its own reasons says nothing about the cookie session; a 401
// that races a token refresh on another BFF replica is a session that is, in fact, alive (the
// replica that won just renewed it). Mobile learned the same lesson in busy-mobile-app#37: a
// feature-level 401 must not log the user out.
//
// So a 401 is checked before it is obeyed. `/bff/user` is the one endpoint that answers from the
// cookie session itself:
//
//   • it says ACTIVE and this request has not been retried → the session is fine, the 401 was
//     about the request: send the request again, once. This IS the silent renewal from the
//     browser's point of view — the BFF refreshes tokens lazily on the gated request, and the
//     user never leaves the page;
//   • it says ACTIVE and the retry 401'd too → the endpoint refuses this user for a reason of its
//     own; hand the error to the caller like any other failure. No redirect: a redirect would be
//     the loop #321 closed, and the session is provably alive;
//   • it says ENDED → the session really is over; the ONE login redirect (guarded by
//     lib/authRedirectGuard.js) is the right answer, and it carries the current URL so the login
//     returns the user to exactly where he was;
//   • it says UNKNOWN (BFF unwell, offline) → we could not ask; failing the request is honest,
//     redirecting into an outage is not (#26). The session monitor owns the "unknown for too
//     long" case.
//
// Pure and injected: the probe, the retry and the request config are arguments, so every branch
// is answerable without axios, a browser, or a real 401.

import { SESSION_ACTIVE, SESSION_ENDED } from '@/lib/sessionState'

/** The mark left on an axios request config once it has had its one retry. */
export const RETRIED_FLAG = '_busyRetriedAfter401'

/** The outcomes a 401 can resolve to. */
export const RETRY = 'retry'
export const REDIRECT = 'redirect'
export const REJECT = 'reject'

/**
 * Has this request already used its retry — or can it not be retried at all? A missing config
 * (a 401 with no request attached) counts as "already retried": there is nothing to send again.
 */
export function alreadyRetried(config) {
  if (!config || typeof config !== 'object') return true
  return config[RETRIED_FLAG] === true
}

/** The same request, marked so a second 401 is not retried again. Does not mutate the original. */
export function markRetried(config) {
  return { ...config, [RETRIED_FLAG]: true }
}

/**
 * The decision table, with no I/O: what to do with a 401 given whether the request has been
 * retried and what `/bff/user` said about the session.
 *
 * @param {{retried: boolean, state: string}} facts
 * @returns {string} one of RETRY, REDIRECT, REJECT
 */
export function decideOn401({ retried, state }) {
  if (state === SESSION_ACTIVE) return retried ? REJECT : RETRY
  if (state === SESSION_ENDED) return REDIRECT
  return REJECT
}

/**
 * The decision with its I/O: ask the probe, then act.
 *
 * `probe` is `auth.probeSession` (async, → `{ state }`); `retry` sends a config again (axios
 * itself). A probe that throws is read as UNKNOWN — the probe promises not to, but a 401 handler
 * that can itself throw would turn one failure into two.
 *
 * @returns {Promise<{action: string, result?: any}>} the action taken, plus the retried
 *   request's result when the action was RETRY.
 */
export async function recoverFrom401({ config, probe, retry }) {
  const retried = alreadyRetried(config)
  let state
  try {
    ;({ state } = await probe())
  } catch {
    state = undefined
  }
  const action = decideOn401({ retried, state })
  if (action === RETRY) {
    return { action, result: await retry(markRetried(config)) }
  }
  return { action }
}

export default recoverFrom401
